import request from "../../service/request";
import constants from "../constants";
import { getResponseObject } from "../utils";
import storageConstants from "../../shared/constants/storage";
import { getLocalStorage } from "../utils/localStorage";
import { safeSendBizEvent } from "../analytics/dynatrace";
import { DT_ATTRIBUTES, DT_EVENTS } from "../constants/dynatrace";
import { API_HEADERS } from "../constants/apiHeaders";

const { APP_ENVIRONMENTS, API_BASE_URL } = constants;
const { DEVICE_ID } = storageConstants;
const ENV = process.env.NODE_ENV || APP_ENVIRONMENTS.PROD;

export function fetchVideoUrl(
  appProvider,
  contentType,
  contentId,
  assetId,
  pin,
  tokeOnly = false,
  cancelTokenSource = null,
  unrestrictedPlay = false
) {
  // CancelToken is deprecated in v0.22.0, will have to update to AbortController if we update Axios in the future
  let url = appProvider.config.avs_api_urls.video_url;

  url = url.replace("$TENANT_URL", appProvider.AGL_TENANT_URL);
  url = url.replace("$AGL_VERSION", appProvider.AGL_Version);
  url = url.replace("$USER_CLUSTER", appProvider.userCluster);
  url = url.replace("$LANG", appProvider.AVS_LANG);
  url = url.replace("$PLATFORM", appProvider.AVS_PLATFORM);
  url = url.replace("$PROPERTY", unrestrictedPlay ? "ALL" : appProvider.AVS_PROPERTY);
  url = url.replace("$CONTENT_TYPE", contentType);
  url = url.replace("$CONTENT_ID", contentId);
  url = url.replace("$ASSET_ID", assetId);

  if (ENV === APP_ENVIRONMENTS.DEV) {
    url = url.replace("https://" + appProvider.AGL_TENANT_URL, API_BASE_URL);
  }

  const deviceId = getLocalStorage(DEVICE_ID);

  if (!unrestrictedPlay && tokeOnly) {
    url += `?${deviceId ? `deviceId=${encodeURIComponent(deviceId)}` : ""}&onlyToken=true`;
  } else {
    url += `?${deviceId ? `deviceId=${encodeURIComponent(deviceId)}` : ""}`;
  }

  let headers;
  if (pin) {
    headers = {
      pcpin: pin,
    };
  }

  return request(
    { url, method: "GET", headers, withCredentials: true, returnResponseObject: true },
    cancelTokenSource,
    appProvider
  )
    .then((response) => {
      if (response?.data?.resultCode === "KO") {
        return Promise.reject(getResponseObject(response));
      } else {
        const cachedOutOfHomeValue = appProvider.isOutOfHome;
        const responseOutOfHomeValue = response?.data?.resultObj?.isOutOfHome;

        const accessType = response?.headers[API_HEADERS.ACCESS_TYPE];
        const serviceProvider = response?.headers[API_HEADERS.SERVICE_PROVIDER];
        const lineType = response?.headers[API_HEADERS.LINE_TYPE];

        if (!!accessType || !!serviceProvider || !!lineType) {
          appProvider.networkAttributes = {
            accessType,
            serviceProvider,
            lineType,
          };
        }

        if (
          responseOutOfHomeValue !== undefined &&
          responseOutOfHomeValue !== null &&
          cachedOutOfHomeValue !== responseOutOfHomeValue
        ) {
          // update out of home value in app cache if it has changed
          appProvider.isOutOfHome = responseOutOfHomeValue;

          // Send Dynatrace In-Home validation event if there is a change in the value
          if (!!accessType || !!serviceProvider || !!lineType) {
            safeSendBizEvent(DT_EVENTS.IN_HOME_VALIDATION, {
              [DT_ATTRIBUTES.NETWORK_IN_HOME]: !responseOutOfHomeValue,
              [DT_ATTRIBUTES.NETWORK_ACCESS_TYPE]: accessType,
              [DT_ATTRIBUTES.NETWORK_SERVICE_PROVIDER]: serviceProvider,
              [DT_ATTRIBUTES.NETWORK_LINE_TYPE]: lineType,
            });
          }
        }
        return response?.data?.resultObj;
      }
    })
    .catch((err) => {
      return Promise.reject(getResponseObject(err));
    });
}

/**
 * Retrieve Video JWT Token. (Currently only applies to live channel content)
 * @param {*} appProvider - App provider to provision calls based on session
 * @param {String} contentType Content Type ( Live only at the moment)
 * @param {String} contentId Content ID of the given video asset
 * @param {Object} cancelTokenSource CancelToken - deprecated in v0.22.0, will have to update to AbortController if we update Axios in the future
 * @return {Promise}
 */
export function fetchVideoToken(appProvider, contentType, contentId, cancelTokenSource = null) {
  let url = appProvider.config.avs_api_urls.video_auth_token;

  url = url.replace("$TENANT_URL", appProvider.AGL_TENANT_URL);
  url = url.replace("$AGL_VERSION", appProvider.AGL_Version);
  url = url.replace("$USER_CLUSTER", "R");
  url = url.replace("$LANG", appProvider.AVS_LANG);
  url = url.replace("$PLATFORM", appProvider.AVS_PLATFORM);
  url = url.replace("$PROPERTY", appProvider.AVS_PROPERTY);
  url = url.replace("$CONTENT_TYPE", contentType);
  url = url.replace("$CONTENT_ID", contentId);

  if (ENV === APP_ENVIRONMENTS.DEV) {
    url = url.replace("https://" + appProvider.AGL_TENANT_URL, API_BASE_URL);
  }

  return request({ url, method: "GET", withCredentials: true }, cancelTokenSource, appProvider)
    .then((response) => {
      if (response?.data?.resultCode === "KO") {
        return Promise.reject(getResponseObject(response));
      } else {
        const cachedOutOfHomeValue = appProvider.isOutOfHome;
        const responseOutOfHomeValue = response?.data?.resultObj?.isOutOfHome;

        const accessType = response?.headers[API_HEADERS.ACCESS_TYPE];
        const serviceProvider = response?.headers[API_HEADERS.SERVICE_PROVIDER];
        const lineType = response?.headers[API_HEADERS.LINE_TYPE];

        if (!!accessType || !!serviceProvider || !!lineType) {
          appProvider.networkAttributes = {
            accessType,
            serviceProvider,
            lineType,
          };
        }

        if (
          responseOutOfHomeValue !== undefined &&
          responseOutOfHomeValue !== null &&
          cachedOutOfHomeValue !== responseOutOfHomeValue
        ) {
          // update out of home value in app cache if it has changed
          appProvider.isOutOfHome = responseOutOfHomeValue;

          // Send Dynatrace In-Home validation event if there is a change in the value
          if (!!accessType || !!serviceProvider || !!lineType) {
            safeSendBizEvent(DT_EVENTS.IN_HOME_VALIDATION, {
              [DT_ATTRIBUTES.NETWORK_IN_HOME]: !responseOutOfHomeValue,
              [DT_ATTRIBUTES.NETWORK_ACCESS_TYPE]: accessType,
              [DT_ATTRIBUTES.NETWORK_SERVICE_PROVIDER]: serviceProvider,
              [DT_ATTRIBUTES.NETWORK_LINE_TYPE]: lineType,
            });
          }
        }
        return response?.data?.resultObj;
      }
    })
    .catch((err) => {
      return Promise.reject(getResponseObject(err));
    });
}
